import { CommunityType } from "@packages/sdk";
import { formatInTimeZone } from "date-fns-tz";

/**
 * The icebreaker "how long have you been attending" answers, mirroring
 * `IcebreakerMembershipLength` in the backend
 * (`src/modules/community/services/IcebreakerService.ts`).
 *
 * The admin dashboard receives the raw enum value on a member and maps it to a
 * label itself, so this list has to stay in step with the backend's. Values are
 * ordered shortest attendance to longest, matching the order the backend sorts
 * `sort_by=membership_length` by.
 */
export enum MembershipLength {
  new = "new",
  less_than_year = "less_than_year",
  one_year = "one_year",
  two_years = "two_years",
  one_to_three_years = "one_to_three_years",
  three_years = "three_years",
  four_years = "four_years",
  three_to_five_years = "three_to_five_years",
  five_or_more_years = "five_or_more_years",
}

/**
 * Lokalise key per membership length.
 *
 * These are the keys the backend already owns for the same values
 * (`IcebreakerService.attendanceOptionLocalizeKey`) rather than a parallel
 * web-only set, so a parish admin reads the same wording the parishioner
 * picked. Do not derive a key from its value: the enum value
 * `five_or_more_years` maps to `attendance_option_five_plus_years`.
 *
 * `new` is included in that rule: the cell reads "I'm new here!" from
 * `attendance_option_new`, the wording the app actually offered, rather than a
 * table-specific label.
 *
 * Only six of these are reachable from the current app: `new`, `one_year`,
 * `two_years`, `three_years`, `four_years` and `five_or_more_years`. The other
 * three are leftovers from an earlier version of the welcome-post question, and
 * are still mapped because stored answers from older app versions carry them -
 * an unmapped value would render blank and read as a bug.
 */
export const MEMBERSHIP_LENGTH_LOCALIZE_KEYS: Record<MembershipLength, string> =
  {
    [MembershipLength.new]: "attendance_option_new",
    [MembershipLength.less_than_year]: "attendance_option_less_than_year",
    [MembershipLength.one_year]: "attendance_option_one_year",
    [MembershipLength.two_years]: "attendance_option_two_years",
    [MembershipLength.one_to_three_years]:
      "attendance_option_one_to_three_years",
    [MembershipLength.three_years]: "attendance_option_three_years",
    [MembershipLength.four_years]: "attendance_option_four_years",
    [MembershipLength.three_to_five_years]:
      "attendance_option_three_to_five_years",
    [MembershipLength.five_or_more_years]: "attendance_option_five_plus_years",
  };

/**
 * The Lokalise key for a member's raw `membership_length`, or `undefined` when
 * there is nothing to show.
 *
 * A large share of members have no value at all — the icebreaker is optional,
 * and anyone who joined before it shipped never saw it — so the Time Attending
 * cell renders blank rather than an em-dash or a "not provided" label.
 * `undefined` is also returned for a value this build does not know about, so a
 * new backend enum member degrades to a blank cell instead of a raw enum value
 * leaking into the table.
 */
export const getMembershipLengthLocalizeKey = (
  membershipLength: string | null | undefined,
): string | undefined =>
  MEMBERSHIP_LENGTH_LOCALIZE_KEYS[membershipLength as MembershipLength];

/**
 * Timezone used to render a member's joined date when the community has no
 * `default_timezone` set.
 *
 * Joined dates are stored as UTC timestamps, so rendering the UTC calendar day
 * is the one choice that is the same for every admin looking at the parish.
 * Falling back to the browser timezone instead would make the same member show
 * a different joined date to two admins in different places.
 */
export const MEMBER_JOINED_DATE_FALLBACK_TIME_ZONE = "UTC";

/**
 * Formats a member's joined date in the community's own timezone.
 *
 * `formatInTimeZone` rather than `format` with a `timeZone` option: date-fns-tz's
 * `format` only consults `timeZone` for the `x`/`X`/`O`/`z` tokens, so a pattern
 * without them silently renders in the runtime timezone (see
 * `@packages/ui/src/utils/dateFormat.ts`).
 *
 * @param joinedAt - UTC ISO-8601 timestamp, or null/undefined for a member with
 *   no recorded join date
 * @param timeZone - the community's `default_timezone`, falling back to UTC
 * @returns the formatted date, or an empty string when there is no date
 */
export const formatMemberJoinedDate = ({
  joinedAt,
  timeZone,
}: {
  joinedAt: string | null | undefined;
  timeZone: string | null | undefined;
}): string => {
  if (!joinedAt) return "";

  const date = new Date(joinedAt);
  if (Number.isNaN(date.getTime())) return "";

  return formatInTimeZone(
    date,
    timeZone || MEMBER_JOINED_DATE_FALLBACK_TIME_ZONE,
    "MMM d, yyyy",
  );
};

/**
 * Whether a community's members table shows the Time Attending column.
 *
 * The column is driven by the parish icebreaker, so only parishes have anything
 * to put in it. Keyed on the community's own type rather than on which page is
 * being rendered, so a child community that is itself a parish gets the column
 * and a basic or small group nested under a parish does not.
 */
export const communityShowsTimeAttending = (
  communityType: CommunityType,
): boolean => communityType === CommunityType.parish;
